import { useCallback, useEffect, useState } from "react";
import { AlertCircle, Banknote, RefreshCw, Save } from "lucide-react";
import { toast } from "sonner";
import { CarregandoBloco, CarregandoCards } from "../../../components/carregando";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { fetchFinancialSummary, fetchSettlements, saveIfoodTaxas } from "../api";

const PERIODOS = [
  { id: 7, label: "7 dias" },
  { id: 30, label: "30 dias" },
  { id: 90, label: "90 dias" },
];

const brl = (v) => (Number(v) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

function Cartao({ titulo, valor, detalhe, destaque }) {
  return (
    <div className={`rounded-xl border p-4 ${destaque ? "bg-green-500/5 border-green-500/30" : "bg-background"}`}>
      <p className="text-xs text-muted-foreground">{titulo}</p>
      <p className={`text-xl font-bold mt-1 tabular-nums ${destaque ? "text-green-600 dark:text-green-400" : ""}`}>{valor}</p>
      {detalhe && <p className="text-[11px] text-muted-foreground mt-0.5">{detalhe}</p>}
    </div>
  );
}

/** Financeiro do iFood: o que entrou, quanto o iFood fica e quanto sobra.
 *  A comissão é estimada pela taxa do contrato da loja enquanto o iFood não
 *  libera o módulo financeiro — o pedido não traz esse número. */
export default function FinanceiroTab() {
  const [dias, setDias] = useState(30);
  const [resumo, setResumo] = useState(null);
  const [repasses, setRepasses] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [taxas, setTaxas] = useState({ taxa_ifood_delivery: "", taxa_merchant_delivery: "" });
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async (d) => {
    setCarregando(true);
    try {
      const [r, s] = await Promise.all([
        fetchFinancialSummary({ days: d }).catch(() => null),
        fetchSettlements({ days: d }).catch(() => null),
      ]);
      setResumo(r);
      setRepasses(s);
      if (r?.taxas) {
        setTaxas({
          taxa_ifood_delivery: String(r.taxas.taxa_ifood_delivery ?? ""),
          taxa_merchant_delivery: String(r.taxas.taxa_merchant_delivery ?? ""),
        });
      }
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(dias); }, [carregar, dias]);

  const salvarTaxas = async () => {
    setSalvando(true);
    try {
      await saveIfoodTaxas({
        taxa_ifood_delivery: taxas.taxa_ifood_delivery,
        taxa_merchant_delivery: taxas.taxa_merchant_delivery,
      });
      toast.success("Taxas salvas");
      await carregar(dias);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível salvar as taxas");
    } finally {
      setSalvando(false);
    }
  };

  const porEntrega = resumo?.por_entrega || {};

  return (
    <div className="space-y-5 max-w-3xl">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex gap-1">
          {PERIODOS.map((p) => (
            <button
              key={p.id} onClick={() => setDias(p.id)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                dias === p.id ? "bg-red-500 text-white" : "bg-muted text-muted-foreground hover:text-foreground"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <Button size="sm" variant="ghost" onClick={() => carregar(dias)} disabled={carregando}>
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${carregando ? "animate-spin" : ""}`} /> Atualizar
        </Button>
      </div>

      {carregando && !resumo ? (
        <div className="space-y-4">
          <CarregandoCards quantos={4} className="grid grid-cols-2 lg:grid-cols-4 gap-3" />
          <CarregandoBloco altura="h-40" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Cartao titulo="Vendas no iFood" valor={brl(resumo?.total_vendas)}
                    detalhe={`${resumo?.total_pedidos || 0} pedidos${resumo?.total_cancelados ? ` · ${resumo.total_cancelados} cancelados` : ""}`} />
            <Cartao titulo="Taxa de entrega" valor={brl(resumo?.total_delivery_fee)} detalhe="cobrada do cliente" />
            <Cartao titulo="Comissão do iFood" valor={brl(resumo?.total_comissao)} detalhe="estimada pela sua taxa" />
            <Cartao titulo="Fica com você" valor={brl(resumo?.total_liquido)} detalhe="vendas menos comissão" destaque />
          </div>

          <div className="rounded-xl border bg-background p-4 space-y-3">
            <p className="font-semibold text-sm flex items-center gap-2"><Banknote className="w-4 h-4" /> Sua taxa no iFood</p>
            <p className="text-[13px] text-muted-foreground">
              O iFood não manda a comissão junto com o pedido. Informe as taxas do seu contrato para o Núcleo calcular
              quanto sobra. É estimativa até o iFood liberar o módulo financeiro.
            </p>
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <Label className="text-xs">Entrega feita pelo iFood (%)</Label>
                <Input
                  type="number" min="0" max="100" step="0.1" className="w-32 h-9"
                  value={taxas.taxa_ifood_delivery}
                  onChange={(e) => setTaxas((t) => ({ ...t, taxa_ifood_delivery: e.target.value }))}
                />
              </div>
              <div>
                <Label className="text-xs">Entrega própria (%)</Label>
                <Input
                  type="number" min="0" max="100" step="0.1" className="w-32 h-9"
                  value={taxas.taxa_merchant_delivery}
                  onChange={(e) => setTaxas((t) => ({ ...t, taxa_merchant_delivery: e.target.value }))}
                />
              </div>
              <Button size="sm" onClick={salvarTaxas} disabled={salvando}>
                <Save className="w-3.5 h-3.5 mr-1" /> Salvar
              </Button>
            </div>
            <div className="grid sm:grid-cols-2 gap-2 pt-1">
              {[["MERCHANT", "Entrega própria"], ["IFOOD", "Entrega do iFood"]].map(([chave, rotulo]) => {
                const b = porEntrega[chave] || {};
                return (
                  <div key={chave} className="rounded-lg bg-muted/40 px-3 py-2 text-[13px]">
                    <p className="font-medium">{rotulo} · {b.taxa}%</p>
                    <p className="text-muted-foreground tabular-nums">
                      {b.pedidos || 0} pedidos · {brl(b.vendas)} · comissão {brl(b.comissao)}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="rounded-xl border bg-background p-4">
            <p className="font-semibold text-sm mb-2">Repasses do iFood</p>
            {repasses?.disponivel ? (
              (repasses.settlements || []).length === 0 ? (
                <p className="text-[13px] text-muted-foreground">Nenhum repasse no período.</p>
              ) : (
                <div className="space-y-1.5">
                  {repasses.settlements.map((s, i) => (
                    <div key={s.id || i} className="flex justify-between text-[13px] border-b last:border-0 py-1.5">
                      <span className="text-muted-foreground">{s.paymentDate || s.calculationDate || "—"}</span>
                      <span className="font-medium tabular-nums">{brl(s.amount ?? s.value)}</span>
                    </div>
                  ))}
                </div>
              )
            ) : (
              <p className="text-[13px] text-muted-foreground flex items-start gap-2">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-amber-500" />
                O iFood ainda não liberou o módulo financeiro para o Núcleo. Enquanto isso, o valor exato dos repasses
                fica no Portal do Parceiro, e aqui você vê a estimativa pela sua taxa.
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
